import * as React from 'react'
import moment from 'moment'

import { TimesheetViewMode } from './TimesheetTableRow'

import TimesheetActionBar from './TimesheetActionBar'
import { DriverBonusAndRewardsModal } from '@/modules/drivers/modals/timesheet/DriverBonusAndRewardsModal'
import { SERVICE_MONTH_FORMAT, SERVICE_DATE_FORMAT } from '@/app/constants/date'
import useTimesheetService from '@/modules/drivers/services/TimesheetService'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'

import { useGroupCalculateHandlers } from '../../views/Group.calculate.handlers'
import { useTimesheetPrefsStore } from '@/modules/drivers/stores/timesheetStore'
import { useDateStore } from '@/modules/drivers/stores/dateStore'
import { createPortal } from 'react-dom'

const getDefaultMonthKey = () => {
  const now = new Date()
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const month = lastMonth.getMonth() + 1
  const year = lastMonth.getFullYear()
  return `${year}-${String(month).padStart(2, '0')}`
}

type Props = {
  companyId: string
  yearMonth: string
  driverId: string
}

export const TimesheetActionBarSingleContainer = ({
  companyId,
  yearMonth,
  driverId,
}: Props) => {
  const [isBonusAndRewardModalOpen, setBonusAndRewardModalOpen] =
    React.useState<boolean>(false)
    
  const toggleBonusAndRewardModal = () =>
    setBonusAndRewardModalOpen((open) => !open)

  const monthFromUrl = React.useMemo(() => {
    if (yearMonth) {
      const parsed = moment(yearMonth, SERVICE_MONTH_FORMAT)
      if (parsed.isValid()) return parsed.format(SERVICE_MONTH_FORMAT)
    }
    return null
  }, [yearMonth])

  // Global month state lives in useDateStore.
  const storeMonth = useDateStore((s) => s.month)
  const setStoreMonth = useDateStore((s) => s.setMonth)

  // Seed store from URL on first mount (deep-link case).
  React.useEffect(() => {
    if (monthFromUrl && monthFromUrl !== useDateStore.getState().month) {
      setStoreMonth(monthFromUrl)
    }
    // run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedMonthKey = storeMonth || monthFromUrl || getDefaultMonthKey()
  const setSelectedMonthKey = setStoreMonth

  const month = React.useMemo(() => {
    const parsed = moment(selectedMonthKey, SERVICE_MONTH_FORMAT)
    return parsed.isValid() ? parsed : moment().startOf('month')
  }, [selectedMonthKey])

  // View mode: 'timesheet' or 'schedule'. Persisted as TimesheetViewMode.
  const storedViewMode = useTimesheetPrefsStore((s) => s.viewMode)
  const setStoredViewMode = useTimesheetPrefsStore((s) => s.setViewMode)
  const viewMode: 'timesheet' | 'schedule' =
    storedViewMode === 'bonus-reward' || storedViewMode === 'schedule'
      ? 'schedule'
      : 'timesheet'
  const setViewMode = React.useCallback(
    (next: 'timesheet' | 'schedule') => {
      const mode: TimesheetViewMode =
        next === 'schedule' ? 'schedule' : 'single-view'
      setStoredViewMode(mode)
    },
    [setStoredViewMode],
  )

  // Schedule year state (default to current year)
  const [scheduleYear, setScheduleYear] = React.useState<number>(() => {
    return new Date().getFullYear()
  })

  // State for remove confirmation modal
  const [showRemoveModal, setShowRemoveModal] = React.useState(false)
  const [timesheetKey, setTimesheetKey] = React.useState(0) // Key to force remount

  // State for calculation info (date and user)
  const [calculationInfo, setCalculationInfo] = React.useState<{
    date: string | null
    user: string | null
    userEmail: string | null
  }>({ date: null, user: null, userEmail: null })

  // Services and contexts
  const { removeTimesheet } = useTimesheetService()
  const { drivers } = useDrivers()
  const { t } = useLocalize()

  // Get driver name for confirmation message
  const driverName = React.useMemo(() => {
    if (!driverId) return ''
    const driver = drivers.find((d) => String(d.id) === String(driverId))
    return (
      driver?.fullName ||
      `${driver?.surname || ''} ${driver?.name || ''}`.trim() ||
      ''
    )
  }, [driverId, drivers])

  const { handleCalculate, scopeBusy } = useGroupCalculateHandlers({
    period: {
      year: month.year(),
      months: [month.month() + 1],
    },
    companyId,
    selectedMonthKey,
    driverIds: [driverId.toString()],
  })

  const handlePrint = React.useCallback(() => {
    // TODO: Implement print logic
    console.log('Print clicked')
  }, [])

  const handleRefresh = React.useCallback(() => {
    // TODO: Implement refresh logic - reload timesheet data
    window.location.reload()
  }, [])

  const handleClose = React.useCallback(() => {
    // Navigate back or close view
    if (history) {
      history.back()
    }
  }, [history])

  const handleSave = React.useCallback(() => {
    // TODO: Implement save logic
    console.log('Save clicked')
  }, [])

  const handleViewToggle = React.useCallback((view: TimesheetViewMode) => {
    if (view === 'bonus-reward') {
      setViewMode('schedule')
    } else {
      setViewMode('timesheet')
    }
  }, [])

  const handleRemove = React.useCallback(() => {
    setShowRemoveModal(true)
  }, [])

  const handleConfirmRemove = React.useCallback(async () => {
    if (!driverId || !companyId) {
      return
    }

    try {
      // Get first day of the month in SERVICE_DATE_FORMAT
      const firstDayOfMonth = month
        .clone()
        .startOf('month')
        .format(SERVICE_DATE_FORMAT)

      await removeTimesheet({
        companyId,
        driverId,
        date: firstDayOfMonth,
      })

      // Show success message
      toast.success({
        text: t('timesheet.remove-timesheet-success'),
        content: t('timesheet.remove-timesheet-success-details'),
      })

      // Close modal
      setShowRemoveModal(false)

      // Force remount of Timesheet component to refresh data
      setTimesheetKey((prev) => prev + 1)

      // Also trigger a refresh
      handleRefresh()
    } catch (error) {
      console.error('Error removing timesheet:', error)
      toast.error({
        text: t('timesheet.remove-timesheet-error'),
        content: t('timesheet.remove-timesheet-error-details'),
      })
      // Close modal even on error
      setShowRemoveModal(false)
    }
  }, [driverId, companyId, month, removeTimesheet, t, handleRefresh])

  if (!driverId) {
    return <div>No driver selected</div>
  }

  return (
    <>
      {isBonusAndRewardModalOpen &&
        createPortal(
          <DriverBonusAndRewardsModal
            companyId={companyId}
            driverId={driverId.toString()}
            month={yearMonth ? new Date(yearMonth).getMonth() + 1 : 0}
            year={yearMonth ? new Date(yearMonth).getFullYear() : 0}
            handleClose={() => setBonusAndRewardModalOpen(false)}
          />,
          document.body,
        )}
      <TimesheetActionBar
        variant="single"
        onCalculate={handleCalculate}
        calculateBusy={scopeBusy.busy && scopeBusy.byOther}
        calculateBusyText={
          scopeBusy.startedBy
            ? `${scopeBusy.startedBy} — ${t('timesheet.calculation-in-progress')}`
            : undefined
        }
        onPrint={handlePrint}
        onRefresh={handleRefresh}
        onRemove={handleRemove}
        onViewToggle={handleViewToggle}
        onBonusAndReward={toggleBonusAndRewardModal}
        currentViewMode={viewMode === 'schedule' ? 'schedule' : 'single-view'}
      />
    </>
  )
}
